import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize, Minus, Plus } from "lucide-react";

const clampK = (k) => Math.min(2.5, Math.max(0.2, k));

export const PanZoom = ({ width, height, children, fitKey, testPrefix = "family" }) => {
  const box = useRef(null);
  const [t, setT] = useState({ x: 0, y: 0, k: 1 });
  const ptrs = useRef(new Map());
  const pinch = useRef(null);

  const fit = useCallback(() => {
    const r = box.current?.getBoundingClientRect();
    if (!r || !width) return;
    const k = clampK(Math.min((r.width - 40) / width, (r.height - 40) / height, 1));
    setT({ k, x: (r.width - width * k) / 2, y: 20 });
  }, [width, height]);
  useEffect(() => { fit(); }, [fit, fitKey]);
  useEffect(() => {
    let w = box.current.clientWidth;
    const ro = new ResizeObserver(() => { if (box.current && box.current.clientWidth !== w) { w = box.current.clientWidth; fit(); } });
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [fit]);

  const zoomAt = (factor, cx, cy) => setT((s) => {
    const k = clampK(s.k * factor);
    return { k, x: cx - ((cx - s.x) * k) / s.k, y: cy - ((cy - s.y) * k) / s.k };
  });
  const center = () => { const r = box.current.getBoundingClientRect(); return [r.width / 2, r.height / 2]; };

  useEffect(() => {
    const el = box.current;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) zoomAt(e.deltaY < 0 ? 1.1 : 0.9, e.clientX - r.left, e.clientY - r.top);
      else setT((s) => ({ ...s, x: s.x - e.deltaX, y: s.y - e.deltaY }));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Capture only once the pointer really moves, so taps on cards/links still click through
  const down = (e) => { ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY }); };
  const move = (e) => {
    const p = ptrs.current.get(e.pointerId);
    if (!p) return;
    if (!p.cap) {
      if (Math.hypot(e.clientX - p.x0, e.clientY - p.y0) < 4) return;
      p.cap = true;
      box.current.setPointerCapture?.(e.pointerId);
    }
    if (ptrs.current.size === 1) setT((s) => ({ ...s, x: s.x + e.clientX - p.x, y: s.y + e.clientY - p.y }));
    ptrs.current.set(e.pointerId, { ...p, x: e.clientX, y: e.clientY });
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const r = box.current.getBoundingClientRect();
      if (pinch.current) zoomAt(dist / pinch.current, (a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top);
      pinch.current = dist;
    }
  };
  const up = (e) => { ptrs.current.delete(e.pointerId); pinch.current = null; };

  return (
    <div ref={box} className="relative h-[70vh] rounded-2xl border border-line bg-inset overflow-hidden touch-none cursor-grab active:cursor-grabbing bg-[radial-gradient(#282d3c_1px,transparent_1px)] [background-size:22px_22px]"
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid={`${testPrefix}-canvas`}>
      <div className="absolute left-0 top-0 origin-top-left" style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.k})`, width, height }}>
        {children}
      </div>
      <div className="absolute right-3 bottom-3 flex flex-col gap-1.5 z-10" onPointerDown={(e) => e.stopPropagation()}>
        {[[Plus, () => zoomAt(1.2, ...center()), "zoom-in"], [Minus, () => zoomAt(1 / 1.2, ...center()), "zoom-out"], [Maximize, fit, "zoom-fit"]].map(([Icon, fn, id]) => (
          <button key={id} onClick={fn} data-testid={`${testPrefix}-${id}-btn`} title={id === "zoom-fit" ? "Fit" : id}
            className="w-9 h-9 rounded-full bg-panel border border-line text-slate-300 hover:text-amber-300 hover:border-amber-500/40 flex items-center justify-center transition-colors">
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>
    </div>
  );
};
